import { HexTile } from './HexTile';
import { HexCoord, HEX_DIRECTIONS, TileType, TileConfig, DEFAULT_TILE_CONFIG } from './HexTypes';

/**
 * ヘクサゴンボード管理クラス
 * 19タイル（中央1 + 第1リング6 + 第2リング12）
 */
export class HexBoard {
  private tiles: Map<string, HexTile>;
  private readonly maxRadius = 2; // 中央から最大距離2
  private tileConfig: TileConfig;
  private replacementTiles: TileType[] = []; // 置き換え候補タイル（互換性のため残す）
  private currentRound: number = 0;

  // 正六角形の6つの角の座標（時計回りで0,2,-2から開始）
  // これがキーマーカーの順序になる
  private readonly cornerCoords: HexCoord[] = [
    { x: 0, y: 2, z: -2 },   // 上（ラウンド1）
    { x: 2, y: 0, z: -2 },   // 右上（ラウンド2）
    { x: 2, y: -2, z: 0 },   // 右下（ラウンド3）
    { x: 0, y: -2, z: 2 },   // 下（ラウンド4）
    { x: -2, y: 0, z: 2 },   // 左下（ラウンド5）
    { x: -2, y: 2, z: 0 }    // 左上（ラウンド6）
  ];
  
  constructor(config?: TileConfig) {
    this.tiles = new Map();
    this.tileConfig = config || DEFAULT_TILE_CONFIG;
    this.initializeTiles();
    this.setupNeighbors();
    this.assignInitialTiles();
  }
  
  /**
   * 19タイルを初期化
   */
  private initializeTiles(): void {
    // 中央タイル（リング0）
    this.addTile({ x: 0, y: 0, z: 0 }, 0);
    
    // 第1リング（6タイル）
    const ring1Coords = this.getRingCoords(1);
    ring1Coords.forEach(coord => this.addTile(coord, 1));
    
    // 第2リング（12タイル）
    const ring2Coords = this.getRingCoords(2);
    ring2Coords.forEach(coord => this.addTile(coord, 2));
  }
  
  /**
   * 指定リングの座標を生成
   */
  private getRingCoords(radius: number): HexCoord[] {
    if (radius === 0) return [{ x: 0, y: 0, z: 0 }];
    
    const coords: HexCoord[] = [];
    
    // 各辺に沿って座標を生成
    for (let i = 0; i < 6; i++) {
      const dir1 = HEX_DIRECTIONS[i];
      const dir2 = HEX_DIRECTIONS[(i + 2) % 6];
      
      for (let j = 0; j < radius; j++) {
        const coord: HexCoord = {
          x: dir1.x * radius + dir2.x * j,
          y: dir1.y * radius + dir2.y * j,
          z: dir1.z * radius + dir2.z * j
        };
        coords.push(coord);
      }
    }
    
    return coords;
  }
  
  /**
   * タイルを追加
   */
  private addTile(coord: HexCoord, ring: number): void {
    const tile = new HexTile(coord, ring);
    this.tiles.set(tile.id, tile);
  }
  
  /**
   * 全タイルの隣接関係を設定
   */
  private setupNeighbors(): void {
    this.tiles.forEach(tile => {
      const neighbors = this.getNeighborsFor(tile);
      tile.setNeighbors(neighbors);
    });
  }
  
  /**
   * 指定タイルの隣接タイルを取得
   */
  private getNeighborsFor(tile: HexTile): HexTile[] {
    const neighbors: HexTile[] = [];
    
    // 通常の隣接タイルを追加
    for (const direction of HEX_DIRECTIONS) {
      const neighborCoord: HexCoord = {
        x: tile.coords.x + direction.x,
        y: tile.coords.y + direction.y,
        z: tile.coords.z + direction.z
      };
      
      const neighborId = HexTile.coordToId(neighborCoord);
      const neighbor = this.tiles.get(neighborId);
      
      if (neighbor) {
        // 中央タイルは隣接リストに含めない（中央を挟んだ隣接を計算するため）
        if (!this.isCenterTile(neighbor.coords)) {
          neighbors.push(neighbor);
        }
      }
    }
    
    // 中央タイルに隣接している場合、中央を挟んだ反対側のタイルも隣接とみなす
    const centerTile = this.getCenterTile();
    const isAdjacentToCenter = HEX_DIRECTIONS.some(dir => {
      const adjCoord = {
        x: tile.coords.x + dir.x,
        y: tile.coords.y + dir.y,
        z: tile.coords.z + dir.z
      };
      return this.isCenterTile(adjCoord);
    });
    
    if (isAdjacentToCenter) {
      // 中央を挟んで反対側のタイルを取得
      const oppositeCoord: HexCoord = {
        x: -tile.coords.x,
        y: -tile.coords.y,
        z: -tile.coords.z
      };
      const oppositeId = HexTile.coordToId(oppositeCoord);
      const oppositeTile = this.tiles.get(oppositeId);
      
      if (oppositeTile && !this.isCenterTile(oppositeTile.coords)) {
        neighbors.push(oppositeTile);
      }
    }
    
    return neighbors;
  }
  
  /**
   * 座標からタイルを取得
   */
  public getTileAt(coord: HexCoord): HexTile | null {
    const id = HexTile.coordToId(coord);
    return this.tiles.get(id) || null;
  }
  
  /**
   * HexCoordからタイルを取得
   */
  public getTileByCoords(coords: HexCoord): HexTile | null {
    return this.getTileAt(coords);
  }
  
  /**
   * IDからタイルを取得
   */
  public getTileById(id: string): HexTile | null {
    return this.tiles.get(id) || null;
  }
  
  /**
   * 全タイルを取得
   */
  public getAllTiles(): HexTile[] {
    return Array.from(this.tiles.values());
  }
  
  /**
   * リングごとのタイルを取得
   */
  public getTilesByRing(ring: number): HexTile[] {
    return this.getAllTiles().filter(tile => tile.ring === ring);
  }
  
  /**
   * 中央タイルを取得
   */
  public getCenterTile(): HexTile {
    return this.getTileAt({ x: 0, y: 0, z: 0 })!;
  }
  
  /**
   * 指定タイルの隣接タイルを取得（キャッシュ利用）
   */
  public getNeighbors(tile: HexTile): HexTile[] {
    return tile.getNeighbors();
  }
  
  /**
   * 2タイル間の距離を計算
   */
  public getDistance(a: HexTile, b: HexTile): number {
    return HexTile.distance(a, b);
  }
  
  /**
   * 全タイルのハイライトをクリア
   */
  public clearHighlights(): void {
    this.tiles.forEach(tile => tile.setHighlighted(false));
  }
  
  /**
   * 全タイルの選択状態をクリア
   */
  public clearSelection(): void {
    this.tiles.forEach(tile => tile.setSelected(false));
  }
  
  /**
   * 複数タイルをハイライト
   */
  public highlightTiles(tiles: HexTile[]): void {
    this.clearHighlights();
    tiles.forEach(tile => tile.setHighlighted(true));
  }
  
  /**
   * タイルを選択
   */
  public selectTile(tile: HexTile): void {
    this.clearSelection();
    tile.setSelected(true);
  }
  
  /**
   * ボードのサイズ（タイル数）を取得
   */
  public getSize(): number {
    return this.tiles.size;
  }
  
  /**
   * 初期タイル配置（全19タイルに配置）
   */
  private assignInitialTiles(): void {
    // タイルプールを作成
    const tilePool: TileType[] = [];

    // 設定に基づいてタイルプールを構築
    Object.entries(this.tileConfig).forEach(([type, count]) => {
      for (let i = 0; i < count; i++) {
        tilePool.push(type as TileType);
      }
    });

    console.log(`tilePool length: ${tilePool.length}`);

    // タイルプールをシャッフル
    this.shuffleArray(tilePool);

    // 全19タイルに配置（最初の19枚を使用）
    const allTiles = this.getAllTiles();
    const tilesToAssign = tilePool.slice(0, 19);

    // 中央タイルを除外（EMPTYのまま）
    const nonCenterTiles = allTiles.filter(tile => !this.isCenterTile(tile.coords));

    // 非中央タイル（18個）に配置
    nonCenterTiles.forEach((tile, index) => {
      if (index < tilesToAssign.length) {
        tile.setTileType(tilesToAssign[index]);
      }
    });

    // 中央タイルは空にする
    const centerTile = this.getCenterTile();
    if (centerTile) {
      centerTile.setTileType(TileType.EMPTY);
    }

    // 残りは今後の互換性のため保持（使用しない）
    this.replacementTiles = tilePool.slice(19);
    console.log(`All tiles assigned. Remaining tiles: ${this.replacementTiles.length}`);
  }
  
  /**
   * 中央タイルかどうか判定
   */
  private isCenterTile(coord: HexCoord): boolean {
    return coord.x === 0 && coord.y === 0 && coord.z === 0;
  }
  
  /**
   * 角のタイルかどうか判定
   */
  private isCornerTile(coord: HexCoord): boolean {
    return this.cornerCoords.some(corner => 
      corner.x === coord.x && corner.y === coord.y && corner.z === coord.z
    );
  }
  
  /**
   * 配列をシャッフル (Fisher-Yates アルゴリズム)
   */
  private shuffleArray<T>(array: T[]): void {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
  }
  
  /**
   * 指定ラウンドのキーマーカーを設定（検証用）
   */
  public setKeyMarkerForRound(round: number): void {
    // 全キーマーカーをクリア
    this.getAllTiles().forEach(tile => {
      tile.setNewlyPlaced(false);
    });

    if (round >= 0 && round < 6) {
      const targetCoord = this.cornerCoords[round];
      const targetTile = this.getTileAt(targetCoord);
      if (targetTile) {
        targetTile.setNewlyPlaced(true);
      }
    }
  }

  /**
   * ラウンドをリセット
   */
  public resetRound(): void {
    this.currentRound = 0;
    this.getAllTiles().forEach(tile => {
      tile.setNewlyPlaced(false);
    });
  }

  /**
   * タイル設定を取得
   */
  public getTileConfig(): TileConfig {
    return { ...this.tileConfig };
  }

  /**
   * ラウンドを進める（キーマーカーの移動のみ）
   */
  public advanceRound(): boolean {
    if (this.currentRound >= 6) {
      console.log(`advanceRound failed: currentRound=${this.currentRound} (max 6 rounds)`);
      return false; // これ以上進められない（ミニゲームは6ラウンド）
    }

    // 前回のキーマーカーをクリア
    this.getAllTiles().forEach(tile => {
      tile.setNewlyPlaced(false);
    });

    // 新しいラウンドのキーマーカーを設定
    const targetCoord = this.cornerCoords[this.currentRound % 6];
    const targetTile = this.getTileAt(targetCoord);

    if (targetTile) {
      // キーマーカーを設定（タイルは既に配置済み）
      targetTile.setNewlyPlaced(true);
      this.currentRound++;
      console.log(`Round ${this.currentRound}: Key marker at (${targetCoord.x}, ${targetCoord.y}, ${targetCoord.z})`);
      return true;
    }

    return false;
  }
  
  /**
   * 現在のラウンド数を取得
   */
  public getCurrentRound(): number {
    return this.currentRound;
  }
  
  /**
   * 残りの置き換え候補タイル数を取得
   */
  public getRemainingReplacementCount(): number {
    return this.replacementTiles.length;
  }
  
  /**
   * タイル配置を再生成
   */
  public regenerate(config: TileConfig): void {
    // マルチプレイヤーモードをリセット
    this.isMultiplayer = false;
    this.boardData = null;
    
    this.tileConfig = config;
    this.currentRound = 0;
    this.replacementTiles = [];
    
    // すべてのタイルのnewlyPlacedフラグをクリア
    this.getAllTiles().forEach(tile => {
      tile.setNewlyPlaced(false);
    });
    
    this.assignInitialTiles();
  }

  /**
   * シードを使ってタイル配置を再生成（マルチプレイヤー用）
   */
  public regenerateWithSeed(seed: string, config: TileConfig): void {
    this.tileConfig = config;
    this.currentRound = 0;
    this.replacementTiles = [];
    
    // すべてのタイルのnewlyPlacedフラグをクリア
    this.getAllTiles().forEach(tile => {
      tile.setNewlyPlaced(false);
    });
    
    // シードを使った配置
    this.assignInitialTilesWithSeed(seed);
  }
  
  /**
   * ボードデータから初期化（マルチプレイヤー用）
   */
  public initFromBoardData(boardData: { 
    tileSequence: string[], 
    roundPlacements: { [key: number]: { coord: string, type: string }[] } 
  }): void {
    console.log('[HexBoard] Initializing from BoardData:', {
      roundsAvailable: Object.keys(boardData.roundPlacements),
      round1Tiles: boardData.roundPlacements[1]?.length || 0
    });
    
    // 全タイルを一旦空にする（シングルプレイヤーのタイルをクリア）
    this.tiles.forEach((tile, coordKey) => {
      tile.setTileType(TileType.EMPTY);
      tile.setNewlyPlaced(false);
    });
    
    // 保存用のデータとフラグを設定
    this.boardData = boardData;
    this.isMultiplayer = true;
    this.currentRound = 0;
    
    // 最初のラウンドのタイルを配置
    this.placeRoundFromData(1);
  }
  
  /**
   * マルチプレイヤーモードかどうか
   */
  public isMultiplayerMode(): boolean {
    return this.isMultiplayer;
  }
  
  /**
   * 次のラウンドのデータから配置（マルチプレイヤー用）
   */
  public placeNextRoundFromData(): boolean {
    if (!this.boardData) {
      console.error('[HexBoard] No board data available');
      return false;
    }
    
    const nextRound = this.currentRound + 1;
    console.log(`[HexBoard] Placing tiles for round ${nextRound}`);
    
    if (!this.boardData.roundPlacements[nextRound]) {
      console.error(`[HexBoard] No placement data for round ${nextRound}`);
      return false;
    }
    
    this.currentRound = nextRound;
    this.placeRoundFromData(nextRound);
    return true;
  }
  
  /**
   * ボードデータからラウンドを配置
   */
  private placeRoundFromData(round: number): void {
    if (!this.boardData || !this.boardData.roundPlacements[round]) {
      console.error(`[HexBoard] No data for round ${round}`);
      return;
    }
    
    const placements = this.boardData.roundPlacements[round];
    console.log(`[HexBoard] Placing ${placements.length} tiles for round ${round}:`, placements);
    
    // 前回のnewlyPlacedをクリア
    this.getAllTiles().forEach(tile => {
      tile.setNewlyPlaced(false);
    });
    
    // 指定された座標にタイルを配置
    placements.forEach(({ coord, type }) => {
      const [x, y, z] = coord.split(',').map(Number);
      const hexCoord = { x, y, z };
      const tile = this.getTileAt(hexCoord);
      if (tile) {
        console.log(`[HexBoard] Placing ${type} at (${x},${y},${z})`);
        tile.setTileType(type as TileType);
        tile.setNewlyPlaced(true);
      } else {
        console.error(`[HexBoard] Tile not found at (${x},${y},${z})`);
      }
    });
    
    this.currentRound = round;
  }
  
  // ボードデータを保存
  private boardData: { 
    tileSequence: string[], 
    roundPlacements: { [key: number]: { coord: string, type: string }[] } 
  } | null = null;
  private isMultiplayer: boolean = false;
  
  /**
   * 初回ラウンドのタイル配置（6個の角に配置）
   */
  private placeInitialRoundTiles(): void {
    if (this.currentRound !== 0) return;
    
    // 6個の角に配置
    for (let i = 0; i < 6 && this.replacementTiles.length > 0; i++) {
      const coord = this.cornerCoords[i];
      const tile = this.getTileAt(coord);
      if (tile) {
        const tileType = this.replacementTiles.shift()!;
        tile.setTileType(tileType);
        tile.setNewlyPlaced(true);
      }
    }
    
    this.currentRound = 1;
  }
  
  /**
   * 新しいラウンドのタイル配置（ミニゲーム用）
   */
  public placeNewRoundTiles(): void {
    if (this.currentRound === 0) {
      this.placeInitialRoundTiles();
      return;
    }
    
    // ラウンド2-6は4個ずつ配置
    const tilesPerRound = 4;
    let placed = 0;
    
    // 全タイルを取得してランダムに配置
    const emptyTiles = Array.from(this.tiles.values()).filter(
      tile => tile.getTileType() === 'EMPTY'
    );
    
    // シャッフル（シードベースにすべき）
    for (let i = emptyTiles.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [emptyTiles[i], emptyTiles[j]] = [emptyTiles[j], emptyTiles[i]];
    }
    
    // 必要な数だけ配置
    for (let i = 0; i < tilesPerRound && i < emptyTiles.length && this.replacementTiles.length > 0; i++) {
      const tile = emptyTiles[i];
      const tileType = this.replacementTiles.shift()!;
      tile.setTileType(tileType);
      tile.setNewlyPlaced(true);
      placed++;
    }
  }
  
  /**
   * シードを使った初期タイル配置
   */
  private assignInitialTilesWithSeed(seed: string): void {
    // シードから擬似乱数生成器を作成
    const seededRandom = this.createSeededRandom(seed);
    
    // タイル種別リストを作成
    const tileTypes: TileType[] = [];
    Object.entries(this.tileConfig).forEach(([type, count]) => {
      if (type !== 'EMPTY' && count > 0) {
        for (let i = 0; i < count; i++) {
          tileTypes.push(type as TileType);
        }
      }
    });
    
    // シャッフル（シード付き）
    for (let i = tileTypes.length - 1; i > 0; i--) {
      const j = Math.floor(seededRandom() * (i + 1));
      [tileTypes[i], tileTypes[j]] = [tileTypes[j], tileTypes[i]];
    }
    
    // 全タイルを一旦空にする
    const allTiles = Array.from(this.tiles.values());
    allTiles.forEach(tile => {
      tile.setTileType(TileType.EMPTY);
    });
    
    // replacementTilesに全タイルを設定（ラウンドごとに配置するため）
    this.replacementTiles = [...tileTypes];
    
    // 最初のラウンドのタイルを配置（6個の角）
    this.currentRound = 0;
    this.placeNewRoundTiles();
  }
  
  /**
   * シードから擬似乱数生成器を作成
   */
  private createSeededRandom(seed: string): () => number {
    let hash = 0;
    for (let i = 0; i < seed.length; i++) {
      const char = seed.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32bit integer
    }
    
    return () => {
      hash = (hash * 1103515245 + 12345) & 0x7fffffff;
      return hash / 0x7fffffff;
    };
  }
  
  /**
   * 旗頭タイル（そのラウンドで新規配置タイルと中央タイルの両方に隣接するタイル）を取得
   */
  public getFlagshipTile(): HexTile | null {
    const newlyPlacedTile = this.getAllTiles().find(tile => tile.isNewlyPlaced());
    if (!newlyPlacedTile) return null;
    
    const centerTile = this.getCenterTile();
    const newlyPlacedNeighbors = newlyPlacedTile.getNeighbors();
    const centerNeighbors = centerTile.getNeighbors();
    
    // 新規配置タイルと中央タイルの両方に隣接するタイルを探す
    for (const tile of this.getAllTiles()) {
      if (tile === newlyPlacedTile || tile === centerTile) continue;
      if (tile.getTileType() === TileType.EMPTY) continue;
      
      const isAdjacentToNewly = newlyPlacedNeighbors.includes(tile);
      const isAdjacentToCenter = centerNeighbors.includes(tile);
      
      if (isAdjacentToNewly && isAdjacentToCenter) {
        return tile;
      }
    }
    
    return null;
  }
  
  /**
   * 現在のラウンドの新規配置タイルが星マーク付きかどうか判定
   */
  public hasStarTileInCurrentRound(): boolean {
    const newlyPlacedTile = this.getAllTiles().find(tile => tile.isNewlyPlaced());
    return newlyPlacedTile ? newlyPlacedTile.hasStar() : false;
  }
  
  /**
   * 次のタイルを配置（ミニゲーム用）
   */
  public placeNextTile(): boolean {
    return this.advanceRound();
  }
  
  /**
   * 現在配置されているタイルを別のタイルに置き換える（ミニゲーム用）
   */
  public replaceCurrentTile(): boolean {
    if (this.currentRound === 0) return false;
    
    // 現在のラウンドのタイルを取得
    const targetCoord = this.cornerCoords[(this.currentRound - 1) % 6];
    const targetTile = this.getTileAt(targetCoord);
    
    if (!targetTile || this.replacementTiles.length === 0) {
      return false;
    }
    
    // 現在のタイルタイプを記録
    const currentType = targetTile.getTileType();
    
    // 置き換え候補からランダムに選択
    const randomIndex = Math.floor(Math.random() * this.replacementTiles.length);
    const newTileType = this.replacementTiles[randomIndex];
    
    // タイルを置き換え
    this.replacementTiles[randomIndex] = currentType; // 元のタイルを候補に戻す
    targetTile.setTileType(newTileType);
    targetTile.setNewlyPlaced(true);
    
    return true;
  }
  
  /**
   * タイルを元に戻す（ミニゲーム用）
   */
  public undoLastTilePlacement(): boolean {
    if (this.currentRound === 0) return false;
    
    const targetCoord = this.cornerCoords[(this.currentRound - 1) % 6];
    const targetTile = this.getTileAt(targetCoord);
    
    if (!targetTile) return false;
    
    // タイルを空に戻す
    const currentType = targetTile.getTileType();
    if (currentType !== TileType.EMPTY) {
      this.replacementTiles.unshift(currentType);
      targetTile.setTileType(TileType.EMPTY);
      targetTile.setNewlyPlaced(false);
      this.currentRound--;
      return true;
    }
    
    return false;
  }
  
  /**
   * デバッグ情報の出力
   */
  public debugInfo(): void {
    console.log(`Total tiles: ${this.tiles.size}`);
    console.log(`Center: ${this.getTilesByRing(0).length} tiles`);
    console.log(`Ring 1: ${this.getTilesByRing(1).length} tiles`);
    console.log(`Ring 2: ${this.getTilesByRing(2).length} tiles`);
    
    // 隣接関係の確認
    const center = this.getCenterTile();
    console.log(`Center neighbors: ${center.getNeighbors().length}`);
    
    // タイルタイプの分布
    const typeCounts: { [key: string]: number } = {};
    this.getAllTiles().forEach(tile => {
      const type = tile.getTileType();
      typeCounts[type] = (typeCounts[type] || 0) + 1;
    });
    console.log('Tile type distribution:', typeCounts);
  }
  
  /**
   * デバッグ出力形式からタイル配置を復元
   * @param debugText デバッグ出力のテキスト
   * @param round ラウンド番号
   */
  public restoreFromDebugOutput(debugText: string, round: number = 1): void {
    // 全タイルをEMPTYにリセット
    this.tiles.forEach(tile => {
      tile.setTileType(TileType.EMPTY);
      tile.setNewlyPlaced(false);
    });
    
    // デバッグテキストをパース
    const lines = debugText.split('\n');
    const tileLines = lines.filter(line => line.trim().match(/^-?\d+,-?\d+,-?\d+:/));
    
    tileLines.forEach(line => {
      const match = line.trim().match(/^(-?\d+),(-?\d+),(-?\d+):\s*(\w+)$/);
      if (match) {
        const x = parseInt(match[1]);
        const y = parseInt(match[2]);
        const z = parseInt(match[3]);
        const typeStr = match[4];
        
        const coord: HexCoord = { x, y, z };
        const tile = this.getTileAt(coord);
        
        if (tile) {
          // タイルタイプを設定
          const tileType = typeStr as TileType;
          if (Object.values(TileType).includes(tileType)) {
            tile.setTileType(tileType);
          }
        }
      }
    });
    
    // ラウンドを設定
    this.currentRound = round;
    
    // ラウンドから新規配置タイルを決定（ラウンド1-12の場合）
    if (round >= 1 && round <= 12) {
      const targetCoord = this.cornerCoords[(round - 1) % 6];
      const targetTile = this.getTileAt(targetCoord);
      if (targetTile && targetTile.getTileType() !== TileType.EMPTY) {
        targetTile.setNewlyPlaced(true);
        console.log(`Newly placed tile at round ${round}: (${targetCoord.x},${targetCoord.y},${targetCoord.z})`);
      }
    }
    
    // 隣接関係を再計算
    this.setupNeighbors();
    
    console.log(`Restored tile configuration for round ${round}`);
  }
}